import React, { useMemo } from 'react';
import { 
  CCard, CCardHeader, CCardBody, CRow, CCol, CButton, 
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CBadge
} from '@coreui/react';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import InteractiveTimeline from '../InteractiveTimeline/InteractiveTimeline';
import dayjs from 'dayjs';
import CIcon from '@coreui/icons-react';
import { cilPeople, cilFlower, cilChartPie, cilList, cilClock } from '@coreui/icons';

const AGE_LABELS = {
  1: "Ребенок (до 12)",
  2: "Подросток (12-18)",
  3: "Молодой (18-35)",
  4: "Взрослый (35-60)",
  5: "Пожилой (60+)"
};

// ЕДИНАЯ ПАЛИТРА ПРИЛОЖЕНИЯ (CoreUI Standard Colors)
const THEME = {
  primary: "#321fdb",   // Синий (Общее/Мужчины)
  success: "#2eb85c",   // Зеленый (Покупки/Букеты)
  info: "#3399ff",      // Голубой
  warning: "#f9b115",   // Оранжевый (Конверсия)
  danger: "#e55353",    // Красный (Женщины)
  secondary: "#6c757d", // Серый (Без букета)
  
  // Оттенки для возрастов (от светлого к темному синему)
  age1: "#90caf9",
  age2: "#42a5f5",
  age3: "#1e88e5",
  age4: "#1565c0",
  age5: "#0d47a1"
};

const VisitorAnalyticsBlock = ({ analytics, onSegmentDoubleClick }) => {
  const totals = analytics?.totals;
  const matrix = analytics?.matrix || [];

  // 1. Диаграмма по полу
  const genderPie = useMemo(() => [
    { id: "Мужчины", label: "Мужчины", value: totals?.male || 0, color: THEME.primary },
    { id: "Женщины", label: "Женщины", value: totals?.female || 0, color: THEME.danger }
  ].filter(d => d.value > 0), [totals]);

  // 2. Диаграмма наличие букета
  const bouquetPie = useMemo(() => [
    { id: "С букетом", label: "С букетом (Покупка)", value: totals?.with_bouquet || 0, color: THEME.success },
    { id: "Без букета", label: "Без букета", value: totals?.no_bouquet || 0, color: THEME.secondary }
  ].filter(d => d.value > 0), [totals]);

  // 3. Диаграмма по возрасту
  const agePie = useMemo(() => [
    { id: "Ребенок", label: "Ребенок (до 12)", value: totals?.child || 0, color: THEME.age1 },
    { id: "Подросток", label: "Подросток (12-18)", value: totals?.teen || 0, color: THEME.age2 },
    { id: "Молодой", label: "Молодой (18-35)", value: totals?.young_adult || 0, color: THEME.age3 },
    { id: "Взрослый", label: "Взрослый (35-60)", value: totals?.adult || 0, color: THEME.age4 },
    { id: "Пожилой", label: "Пожилой (60+)", value: totals?.senior || 0, color: THEME.age5 }
  ].filter(d => d.value > 0), [totals]);

  // 4. Столбчатый график: Покупки букетов по возрастам
  const ageBarData = useMemo(() => {
    return matrix.map(row => ({
      age: row.age_title.split(' ')[0], // Короткое название
      'С букетом': row.group_bouquets,
      'Без букета': row.total - row.group_bouquets,
    }));
  }, [matrix]);

  // Метаданные для таймлайна выходов
  const outMeta = { "-10": { label: "Вышло человек", color: THEME.success }, "default": { label: "", color: "#D1D5DB" }};

  if (!totals) return null;

  return (
    <div>
      {/* КАРТОЧКИ ГЛАВНЫХ МЕТРИК (В едином стиле CoreUI border-top-3) */}
      <CRow className="mb-4">
        <CCol md={4}>
          <CCard className="h-100 shadow-sm border-top-primary border-top-3">
            <CCardBody className="text-center">
              <div className="text-muted text-uppercase fw-bold small mb-2">
                <CIcon icon={cilPeople} className="me-2"/>Трафик гостей (Выход)
              </div>
              <div className="fs-2 fw-bold text-dark">{totals.out} <span className="fs-6 fw-normal text-muted">чел.</span></div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={4}>
          <CCard className="h-100 shadow-sm border-top-success border-top-3">
            <CCardBody className="text-center">
              <div className="text-muted text-uppercase fw-bold small mb-2">
                <CIcon icon={cilFlower} className="me-2"/>Покупки (С букетом)
              </div>
              <div className="fs-2 fw-bold text-dark">{totals.with_bouquet} <span className="fs-6 fw-normal text-muted">чел.</span></div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={4}>
          <CCard className="h-100 shadow-sm border-top-warning border-top-3">
            <CCardBody className="text-center">
              <div className="text-muted text-uppercase fw-bold small mb-2">
                <CIcon icon={cilChartPie} className="me-2"/>Конверсия продаж
              </div>
              <div className="fs-2 fw-bold text-dark">{totals.conversion_rate}%</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* ЕДИНЫЙ БЛОК ДЛЯ КРУГОВЫХ ДИАГРАММ */}
      <CCard className="mb-4 shadow-sm border-top-info border-top-3">
        <CCardHeader className="bg-white fw-bold">Демография и общая статистика</CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md={4} className="border-end">
              <h6 className="text-center text-muted small text-uppercase fw-bold mb-3">Пол гостей</h6>
              <div style={{ height: "200px" }}>
                {genderPie.length > 0 ? (
                  <ResponsivePie
                    data={genderPie}
                    innerRadius={0.6}
                    padAngle={1}
                    cornerRadius={3}
                    colors={{ datum: "data.color" }}
                    enableArcLinkLabels={false}
                    arcLabel={d => `${d.value}`}
                  />
                ) : <div className="text-center text-muted mt-5">Нет данных</div>}
              </div>
            </CCol>

            <CCol md={4} className="border-end">
              <h6 className="text-center text-muted small text-uppercase fw-bold mb-3">Наличие покупки</h6>
              <div style={{ height: "200px" }}>
                {bouquetPie.length > 0 ? (
                  <ResponsivePie
                    data={bouquetPie}
                    innerRadius={0.6}
                    padAngle={1}
                    cornerRadius={3}
                    colors={{ datum: "data.color" }}
                    enableArcLinkLabels={false}
                    arcLabel={d => `${Math.round(d.value/totals.out*100)}%`}
                  />
                ) : <div className="text-center text-muted mt-5">Нет данных</div>}
              </div>
            </CCol>

            <CCol md={4}>
              <h6 className="text-center text-muted small text-uppercase fw-bold mb-3">Возрастные группы</h6>
              <div style={{ height: "200px" }}>
                {agePie.length > 0 ? (
                  <ResponsivePie
                    data={agePie}
                    innerRadius={0.6}
                    padAngle={1}
                    cornerRadius={3}
                    colors={{ datum: "data.color" }}
                    enableArcLinkLabels={false}
                    arcLabel={d => `${d.value}`}
                  />
                ) : <div className="text-center text-muted mt-5">Нет данных</div>}
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* СТОЛБЧАТЫЙ ГРАФИК ПОКУПОК ПО ВОЗРАСТАМ */}
      <CCard className="mb-4 shadow-sm">
        <CCardHeader className="bg-white fw-bold">Распределение покупок по возрастам</CCardHeader>
        <CCardBody style={{ height: "300px" }}>
          <ResponsiveBar
            data={ageBarData}
            keys={['С букетом', 'Без букета']}
            indexBy="age"
            margin={{ top: 10, right: 130, bottom: 40, left: 40 }}
            padding={0.4}
            borderRadius={2}
            colors={[THEME.success, THEME.secondary]}
            axisBottom={{ tickSize: 0, tickPadding: 10 }}
            axisLeft={{ tickSize: 0, tickPadding: 10 }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 120,
                itemWidth: 100,
                itemHeight: 20,
                symbolSize: 12,
                symbolShape: 'circle'
              }
            ]}
          />
        </CCardBody>
      </CCard>

      {/* ПОДРОБНАЯ МАТРИЦА ПОРТРЕТА ПОКУПАТЕЛЯ */}
      <CCard className="mb-4 shadow-sm border-0">
        <CCardHeader className="bg-white fw-bold">
          Подробный портрет покупателей (Пол x Возраст x Букет)
        </CCardHeader>
        <CCardBody className="p-0">
          <CTable hover responsive bordered align="middle" className="text-center mb-0">
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell rowSpan="2" className="align-middle">Возрастная группа</CTableHeaderCell>
                <CTableHeaderCell colSpan="2" className="text-primary">Мужчины</CTableHeaderCell>
                <CTableHeaderCell colSpan="2" className="text-danger">Женщины</CTableHeaderCell>
                <CTableHeaderCell rowSpan="2" className="align-middle">Всего гостей</CTableHeaderCell>
                <CTableHeaderCell rowSpan="2" className="align-middle text-success">Купили букет</CTableHeaderCell>
                <CTableHeaderCell rowSpan="2" className="align-middle text-warning">Конверсия</CTableHeaderCell>
              </CTableRow>
              <CTableRow>
                <CTableHeaderCell className="small text-success">С букетом</CTableHeaderCell>
                <CTableHeaderCell className="small text-secondary">Без букета</CTableHeaderCell>
                <CTableHeaderCell className="small text-success">С букетом</CTableHeaderCell>
                <CTableHeaderCell className="small text-secondary">Без букета</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {matrix.map((row) => (
                <CTableRow key={row.age_id}>
                  <CTableDataCell className="fw-bold text-dark">{row.age_title}</CTableDataCell>
                  <CTableDataCell className="text-success fw-bold">{row.male_bouquet}</CTableDataCell>
                  <CTableDataCell className="text-muted">{row.male_no_bouquet}</CTableDataCell>
                  <CTableDataCell className="text-success fw-bold">{row.female_bouquet}</CTableDataCell>
                  <CTableDataCell className="text-muted">{row.female_no_bouquet}</CTableDataCell>
                  <CTableDataCell className="fw-bold bg-light">{row.total}</CTableDataCell>
                  <CTableDataCell className="text-success fw-bold bg-light">{row.group_bouquets}</CTableDataCell>
                  <CTableDataCell className="bg-light">
                    <span 
                      className={`badge ${row.group_conv > 30 ? 'bg-success' : row.group_conv > 15 ? 'bg-warning' : 'bg-secondary'}`}
                    >
                      {row.group_conv}%
                    </span>
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </CCardBody>
      </CCard>

      {/* ТАЙМЛАЙН ВЫХОДОВ */}
      <CCard className="mb-4 shadow-sm border-top-primary border-top-3">
        <CCardHeader className="bg-white fw-bold">
          <CIcon icon={cilClock} className="me-2 text-primary" /> Интенсивность потока выходов по времени (минуты)
        </CCardHeader>
        <CCardBody>
          <InteractiveTimeline 
            timelineData={analytics.timeline_out} 
            dateRange={analytics.date_range} 
            classIdToMeta={outMeta} 
            onItemDoubleClick={onSegmentDoubleClick} 
          />
        </CCardBody>
      </CCard>

      {/* ТАБЛИЦА ЛОГОВ */}
      <CCard className="mb-4 shadow-sm border-0">
        <CCardHeader className="bg-white d-flex justify-content-between align-items-center">
          <strong className="fw-bold"><CIcon icon={cilList} className="me-2"/>Детальный лог выходов ({analytics.raw_logs?.length || 0} зап.)</strong>
        </CCardHeader>
        <CCardBody style={{ maxHeight: "400px", overflowY: "auto" }} className="p-0">
          {analytics.raw_logs && analytics.raw_logs.length > 0 ? (
            <CTable striped hover responsive align="middle" className="text-center mb-0">
              <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <CTableRow>
                  <CTableHeaderCell>Время выхода</CTableHeaderCell>
                  <CTableHeaderCell>ID Трека</CTableHeaderCell>
                  <CTableHeaderCell>Пол</CTableHeaderCell>
                  <CTableHeaderCell>Возраст</CTableHeaderCell>
                  <CTableHeaderCell>Результат</CTableHeaderCell>
                  <CTableHeaderCell>Уверенность</CTableHeaderCell>
                  <CTableHeaderCell>Действие</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {analytics.raw_logs.map((log) => (
                  <CTableRow key={log.id}>
                    <CTableDataCell className="fw-bold">{dayjs(log.timestamp).format('DD.MM.YYYY HH:mm:ss')}</CTableDataCell>
                    <CTableDataCell><code>#{log.track_id}</code></CTableDataCell>
                    <CTableDataCell className={log.gender === 'm' ? "text-primary" : "text-danger"}>
                      {log.gender === 'm' ? "Мужчина" : "Женщина"}
                    </CTableDataCell>
                    <CTableDataCell>{AGE_LABELS[log.age_group] || "Не определен"}</CTableDataCell>
                    <CTableDataCell>
                      {log.has_bouquet ? (
                        <CBadge color="success">С букетом</CBadge>
                      ) : (
                        <CBadge color="secondary">Без букета</CBadge>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-muted">{(log.confidence * 100).toFixed(0)}%</CTableDataCell>
                    <CTableDataCell>
                      <CButton size="sm" color="primary" variant="outline" onClick={() => onSegmentDoubleClick(null, log.timestamp)}>
                        Смотреть
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          ) : (
            <div className="p-4 text-center text-muted">Нет зафиксированных выходов за выбранный период.</div>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
};

export default VisitorAnalyticsBlock;